/**
 * main.tsx — application entry point.
 *
 * Provider order (outer to inner):
 *   QueryClientProvider → ThemeProvider → App (with RouterProvider) → Toaster
 *
 * Architecture note (CLAUDE.md): BrowserRouter is replaced by createBrowserRouter
 * inside router.tsx; RouterProvider is called inside App.tsx.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';

import App from './App';
import ThemeProvider from './components/theme/ThemeProvider';
import { FilePreviewModal } from './components/domain/FilePreviewModal';
import { resetClientSession } from './lib/auth/session';
import { queryClient } from './lib/query-client';
import './styles/globals.css';

// Listen for auth:logout events emitted by the API client interceptor when the
// refresh cookie is gone/expired (avoids circular imports between
// api/client.ts and the auth store). Fully tear down the session so a forced
// logout leaves nothing behind for the next user; clearing the auth store also
// re-renders RequireAuth, which then redirects to /login.
window.addEventListener('auth:logout', () => {
  resetClientSession();
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found in index.html');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <App />
        <FilePreviewModal />
        <Toaster
          richColors
          closeButton
          position="top-right"
          toastOptions={{
            duration: 5000,
          }}
        />
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);
